<template>
  <div class="hint-section">
    <button
      type="button"
      class="hint-toggle"
      :aria-expanded="expanded"
      @click="toggle"
    >
      <i :class="['fas', expanded ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
      <span class="toggle-label">{{ t('video.subtitle.modal.autoGenerate.hint.toggle') }}</span>
    </button>
    <p class="hint-description">
      {{ t('video.subtitle.modal.autoGenerate.hint.description') }}
    </p>
    <div v-if="expanded" class="hint-body">
      <Field
        :title="t('video.subtitle.modal.autoGenerate.hint.glossary.label')"
        :helpText="t('video.subtitle.modal.autoGenerate.hint.glossary.help')"
        className="hint-field"
      >
        <div class="chip-input-wrapper">
          <vue-tags-input
            v-model="glossaryDraft"
            :tags="glossaryTags"
            :add-on-key="[13, 188]"
            :separators="[',']"
            :max-tags="MAX_GLOSSARY"
            :validation="glossaryValidation"
            :placeholder="glossaryPlaceholder"
            :autocomplete-min-length="0"
            autocomplete="off"
            @tags-changed="onGlossaryChanged"
          />
        </div>
        <div :class="['counter', counterClass(glossaryCount, MAX_GLOSSARY)]">
          {{ t('video.subtitle.modal.autoGenerate.hint.glossary.counter', { count: glossaryCount }) }}
        </div>
      </Field>
      <Field
        :title="t('video.subtitle.modal.autoGenerate.hint.context.label')"
        :helpText="t('video.subtitle.modal.autoGenerate.hint.context.help')"
        className="hint-field"
      >
        <TextArea
          v-model="contextLocal"
          :placeholder="t('video.subtitle.modal.autoGenerate.hint.context.placeholder')"
          :rows="4"
          :maxLength="MAX_CONTEXT_LEN"
        />
        <div :class="['counter', counterClass(contextCount, MAX_CONTEXT_LEN)]">
          {{ t('video.subtitle.modal.autoGenerate.hint.context.counter', { count: contextCount }) }}
        </div>
      </Field>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { VueTagsInput } from '@vojtechlanka/vue-tags-input';
import Field from '@/modules/shared/components/molecules/field/Field.vue';
import TextArea from '@/modules/shared/components/atoms/textArea/TextArea.vue';

interface Props {
  glossary: string[];
  context: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:glossary': [value: string[]];
  'update:context': [value: string];
}>();

const { t } = useI18n();

const MAX_GLOSSARY = 50;
const MAX_TERM_LEN = 80;
const MAX_CONTEXT_LEN = 2000;

const expanded = ref<boolean>(props.glossary.length > 0 || props.context.length > 0);

const glossaryDraft = ref<string>('');

const glossaryTags = computed(() => props.glossary.map((text) => ({ text })));
const glossaryCount = computed(() => props.glossary.length);
const contextCount = computed(() => props.context.length);

const glossaryPlaceholder = computed(() => (
  props.glossary.length === 0 ?
    t('video.subtitle.modal.autoGenerate.hint.glossary.placeholder')
    : ''
));

const glossaryValidation = [
  {
    classes: 'ti-invalid',
    rule: (tag: { text: string }) => tag.text.length > MAX_TERM_LEN,
  },
];

function onGlossaryChanged(newTags: Array<{ text: string }>) {
  const accepted = newTags
    .map((tag) => tag.text)
    .filter((text) => text.length > 0 && text.length <= MAX_TERM_LEN)
    .slice(0, MAX_GLOSSARY);
  emit('update:glossary', accepted);
  glossaryDraft.value = '';
}

const contextLocal = computed({
  get: () => props.context,
  set: (value: string) => emit('update:context', value),
});

function counterClass(count: number, max: number): string {
  const pct = count / max;
  if (pct >= 1) return 'counter--red';
  if (pct >= 0.8) return 'counter--amber';
  return 'counter--neutral';
}

function toggle() {
  expanded.value = !expanded.value;
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/global-variables.scss';

.hint-section {
  margin-top: $spacing-base;
  padding-top: $spacing-base;
  border-top: 1px solid $grey-200;
}

.hint-toggle {
  display: flex;
  align-items: center;
  gap: $spacing-8;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: $grey-800;
  font-size: $font-level-6;
  font-weight: $font-weight-base;

  .fas {
    color: $grey-600;
    font-size: 12px;
  }
}

.hint-description {
  margin: $spacing-4 0 0 0;
  color: $grey-600;
  font-size: $font-level-8;
}

.hint-body {
  margin-top: $spacing-base;
  display: flex;
  flex-direction: column;
  gap: $spacing-base;
}

.chip-input-wrapper {
  :deep(.vue-tags-input) {
    border: 1px solid #e8e8e8;
    border-radius: 3px;
    max-width: 100%;

    .ti-input {
      border: none;
    }

    .ti-tag {
      border-radius: 5px;
      padding: 5px 8px 4px;
      font-size: 13px;
      background-color: #EBEDEF;
      color: $grey-700;

      &.ti-invalid {
        background-color: $danger-pale;
        color: #E14242;
      }
    }
  }
}

.counter {
  margin-top: $spacing-4;
  font-size: $font-level-8;
  text-align: right;

  &--neutral {
    color: $grey-600;
  }

  &--amber {
    color: #C77800;
  }

  &--red {
    color: $danger;
  }
}
</style>
